import React, { useEffect, useState } from 'react';
import '@/App.css';
import './responsive.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Reservas from './pages/Reservas';
import Ingresos from './pages/Ingresos';
import Gastos from './pages/Gastos';
import Calendario from './pages/Calendario';
import Configuracion from './pages/Configuracion';
import Datos from './pages/Datos';
import Clientes from './pages/Clientes';
import MensajesWhatsApp from './pages/MensajesWhatsApp';
import Contratos from './pages/Contratos';
import RegistroCliente from './pages/RegistroCliente';
import FanPage from './pages/FanPage';
import SolicitudesReserva from './pages/SolicitudesReserva';
import GastoRapido from './pages/GastoRapido';
import Login from './pages/Login';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PropiedadProvider } from './contexts/PropiedadContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Componente para proteger rutas
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#8FE0FF] to-[#F7E8C9] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E3A53] mx-auto mb-4"></div>
          <div className="text-xl font-semibold text-[#0E3A53]">Cargando...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

// Componente principal interno con acceso a Auth
const AppContent = () => {
  const { isAuthenticated } = useAuth();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const isNew = window.location.pathname.startsWith('/_new') || (process.env.PUBLIC_URL && process.env.PUBLIC_URL.includes('_new'));
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = 'image/svg+xml';
    if (isNew) {
      link.href = `${process.env.PUBLIC_URL || ''}/favicon-red.svg`;
    } else {
      link.href = `${process.env.PUBLIC_URL || ''}/favicon.svg`;
    }
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Seed data inicial
        await axios.post(`${API}/seed`);
        setInitialized(true);
      } catch (error) {
        console.log('App ya inicializada o error:', error.response?.data?.message);
        setInitialized(true);
      }
    };
    initializeApp();
  }, []);

  if (!initialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#8FE0FF] to-[#F7E8C9] flex items-center justify-center">
        <div className="text-2xl font-semibold text-[#0E3A53]">Cargando R3NTapp...</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Rutas públicas - Fan Page y Registro */}
      <Route path="/" element={<FanPage />} />
      <Route path="/propiedad/:id_propiedad" element={<FanPage />} />
      <Route path="/registro" element={<RegistroCliente />} />
      <Route path="/gasto-rapido" element={<GastoRapido />} />

      {/* Rutas administrativas */}
      <Route path="/admin/login" element={
        isAuthenticated ? <Navigate to="/admin/dashboard" replace /> : <Login />
      } />

      {/* Rutas protegidas - requieren autenticación */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <PropiedadProvider>
            <Layout />
          </PropiedadProvider>
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="reservas" element={<Reservas />} />
        <Route path="ingresos" element={<Ingresos />} />
        <Route path="gastos" element={<Gastos />} />
        <Route path="calendario" element={<Calendario />} />
        <Route path="clientes" element={<Clientes />} />
        <Route path="mensajes-whatsapp" element={<MensajesWhatsApp />} />
        <Route path="contratos" element={<Contratos />} />
        <Route path="solicitudes-reserva" element={<SolicitudesReserva />} />
        <Route path="datos" element={<Datos />} />
        <Route path="configuracion" element={<Configuracion />} />
      </Route>

      {/* Ruta por defecto */}
      <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter basename={process.env.PUBLIC_URL}>
          <AppContent />
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthProvider>
  );
}

export default App;
